
import { Vue, Component, Mixins } from "vue-property-decorator";
import { Size } from "../../../domain/drawing";

interface IElementResizedHandleable {
  name: string;
  handler: (newSize: Size, oldSize: Size) => void;
  lastHandleSize: Size;
}

class ElementResizedWatcher {
  constructor(source: Vue) {
    this._handlers = [];
    this._source = source;
    this._onWindowResized = e => this.handleResized();
    window.addEventListener("resize", this._onWindowResized);
    // chromeもIE11も要素のresizeイベントは発生しない模様.
    // wideモニタで全画面表示であればwindowリサイズ以外でサイズが変わる事はなさそうだが、
    // 画面幅狭めて利用してるようなユースケースでも救えるようインターバルタイマでのリカバリも行う.
    // あくまでいざという時のリカバリなのでタイマは長めに.
    // (getBoundingClientRect()はあまり軽くない処理のようなので呼ばれ過ぎてパフォーマンスに影響するのを避ける.)
    this._timer = setInterval(() => this.handleResized(), 3000);
  }

  private _handlers: IElementResizedHandleable[];
  private readonly _source: Vue;
  private readonly _onWindowResized: (e: Event) => void;
  private readonly _timer: NodeJS.Timeout;

  add(name: string, handler: (newSize: Size, oldSize: Size) => void): Size {
    if (!name || !handler) {
      return;
    }
    const instance = {
      name,
      handler,
      lastHandleSize: this.getElementSize(name),
    };
    this._handlers.push(instance);
    return instance.lastHandleSize;
  }

  private getElementSize(name: string): Size {
    const element = this._source.$refs[name] as HTMLElement;
    if (!element) {
      return null;
    }
    const rectangle = element.getBoundingClientRect();
    return rectangle
      ? new Size(rectangle.width, rectangle.height)
      : null;
  }

  private handleResized(): void {
    for (const handler of this._handlers) {
      const size = this.getElementSize(handler.name);
      if (!Size.areEqual(size, handler.lastHandleSize)) {
        handler.handler(size, handler.lastHandleSize);
        handler.lastHandleSize = size;
      }
    }
  }

  destroy(): void {
    window.removeEventListener("resize", this._onWindowResized);
    clearInterval(this._timer);
  }
}

@Component
export default class ComponentWatchableMixin extends Mixins() {
  mounted(): void {
    this._elementResizedWatcher = new ElementResizedWatcher(this);
  }

  private _elementResizedWatcher: ElementResizedWatcher;

  beforeDestroy(): void {
    this._elementResizedWatcher.destroy();
  }

  watchHtmlElementResized(name: string, handler: (newSize: Size, oldSize: Size) => void): Size {
    return this._elementResizedWatcher.add(name, handler);
  }
}
